import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MdDashboard,
  MdPerson,
  MdBusiness,
  MdPayment,
  MdReceipt,
  MdVerifiedUser,
  MdSwapHoriz,
  MdPhoneAndroid,
  MdHistory,
  MdSettings,
  MdLocationOn,
  MdGroup,
  MdSupport,
} from 'react-icons/md';
import { API_BASE_URL, API_CONFIG } from '../config/api';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
import DashboardView from './components/DashboardView';
import UsersView from './components/UsersView';
import ZonesView from './components/ZonesView';
import GroupsView from './components/GroupsView';
import RevenueOfficersView from './components/RevenueOfficersView';
import PaymentMethodsView from './components/PaymentMethodsView';
import ReceiptsView from './components/ReceiptsView';
import TransactionsView from './components/TransactionsView';
import AuditLogsView from './components/AuditLogsView';
import TradersView from './components/TradersView';
import DevicesView from './components/DevicesView';
import SupportTicketsView from './components/SupportTicketsView';
import ChatButton from './components/ChatButton';
import ChatModal from './components/ChatModal';
import AddZoneModal from './components/AddZoneModal';
import EditZoneModal from './components/EditZoneModal';
import ViewZoneModal from './components/ViewZoneModal';
import AddGroupModal from './components/AddGroupModal';
import ViewGroupModal from './components/ViewGroupModal';
import OfficerPerformanceModal from './components/OfficerPerformanceModal';
import ReassignOfficerModal from './components/ReassignOfficerModal';
import UserDetailsModal from './components/UserDetailsModal';
import ViewRevenueOfficerModal from './components/ViewRevenueOfficerModal';
import ViewTraderModal from './components/ViewTraderModal';
import AddTraderModal from './components/AddTraderModal';
import EditTraderModal from './components/EditTraderModal';
import ReassignTraderModal from './components/ReassignTraderModal';
import AddUserModal from './components/AddUserModal';
import ResultModal from './components/ResultModal';
import EditGroupModal from './components/EditGroupModal';
import AddRevenueOfficerModal from './components/AddRevenueOfficerModal';
import EditRevenueOfficerModal from './components/EditRevenueOfficerModal';
import EditUserModal from './components/EditUserModal';

const MMDAAdminDashboard = ({ user, onUserChange }) => {
  const [selectedTable, setSelectedTable] = useState({ name: 'Dashboard', endpoint: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagedFilteredData, setPagedFilteredData] = useState([]);

  // Separate search states for each view
  const [searchTerms, setSearchTerms] = useState({
    'My Zones': '',
    'Groups': '',
    'Revenue Officers': '',
    'Traders': '',
    'Users': '',
    'Devices': '',
    'Payment Methods': '',
    'Receipts': '',
    'Transactions': '',
    'Audit Logs': ''
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({});

  // Helper functions for search terms
  const getCurrentSearchTerm = () => searchTerms[selectedTable.name] || '';
  const setCurrentSearchTerm = (term) => {
    setSearchTerms(prev => ({
      ...prev,
      [selectedTable.name]: term
    }));
  };

  // Modal states
  const [isAddZoneModalOpen, setIsAddZoneModalOpen] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [isEditZoneModalOpen, setIsEditZoneModalOpen] = useState(false);
  const [viewZone, setViewZone] = useState(null);
  const [isViewZoneModalOpen, setIsViewZoneModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [viewGroup, setViewGroup] = useState(null);
  const [isViewGroupModalOpen, setIsViewGroupModalOpen] = useState(false);
  const [isAddRevenueOfficerModalOpen, setIsAddRevenueOfficerModalOpen] = useState(false);
  const [editRevenueOfficer, setEditRevenueOfficer] = useState(null);
  const [isEditRevenueOfficerModalOpen, setIsEditRevenueOfficerModalOpen] = useState(false);
  const [performanceOfficer, setPerformanceOfficer] = useState(null);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [reassignOfficer, setReassignOfficer] = useState(null);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [isViewUserModalOpen, setIsViewUserModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [viewOfficer, setViewOfficer] = useState(null);
  const [isViewOfficerModalOpen, setIsViewOfficerModalOpen] = useState(false);
  const [viewTrader, setViewTrader] = useState(null);
  const [isViewTraderModalOpen, setIsViewTraderModalOpen] = useState(false);
  const [isAddTraderModalOpen, setIsAddTraderModalOpen] = useState(false);
  const [reassignTrader, setReassignTrader] = useState(null);
  const [isReassignTraderModalOpen, setIsReassignTraderModalOpen] = useState(false);
  const [editTrader, setEditTrader] = useState(null);
  const [isEditTraderModalOpen, setIsEditTraderModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  // Chat and Support modal states
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // Result modal states
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalData, setResultModalData] = useState({
    type: 'success',
    operation: 'Created',
    itemType: 'Item',
    itemName: '',
    details: null
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');

  // Result modal helper functions
  const showResultMessage = (type, operation, itemType, itemName = '', details = null) => {
    setResultModalData({
      type,
      operation,
      itemType,
      itemName,
      details
    });
    setShowResultModal(true);
  };

  const handleOperationSuccess = (operation, itemType, itemName = '', details = null) => {
    showResultMessage('success', operation, itemType, itemName, details);
    fetchData(); // Refresh data after successful operation
  };

  const handleOperationError = (operation, itemType, itemName = '', error = null) => {
    const details = error ? `Error: ${error.message || error}` : null;
    showResultMessage('error', operation, itemType, itemName, details);
  };

  // Notification handlers
  const handleOpenChat = () => {
    setIsChatModalOpen(true);
  };

  const handleOpenSupport = () => {
    setSelectedTable({ name: 'Support', endpoint: '/support' });
  };

  const ITEMS_PER_PAGE = 10;


  // MMDA-specific navigation items
  const tables = [
    { name: 'Dashboard', endpoint: '', icon: <MdDashboard size={20} /> },
    {
      name: 'Zone Management',
      icon: <MdLocationOn size={20} />,
      subItems: [
        { name: 'My Zones', endpoint: '/zones', icon: <MdLocationOn size={18} /> },
        { name: 'Groups', endpoint: '/zonal-groups', icon: <MdGroup size={18} /> },
        { name: 'Revenue Officers', endpoint: '/revenue-officers', icon: <MdVerifiedUser size={18} /> },
        { name: 'Traders', endpoint: '/traders', icon: <MdBusiness size={18} /> },
      ],
    },
    { name: 'Users', endpoint: '/users', icon: <MdPerson size={20} /> },
    { name: 'Devices', endpoint: '/devices', icon: <MdPhoneAndroid size={20} /> },
    { name: 'Payment Methods', endpoint: '/payment-methods', icon: <MdPayment size={20} /> },
    { name: 'Receipts', endpoint: '/receipts', icon: <MdReceipt size={20} /> },
    { name: 'Transactions', endpoint: '/transactions', icon: <MdSwapHoriz size={20} /> },
    { name: 'Audit Logs', endpoint: '/audit-logs', icon: <MdHistory size={20} /> },
    { name: 'Support', endpoint: '/support', icon: <MdSupport size={20} /> },
  ];

  // Get user's MMDA ID for filtering
  const userMmdaId = user.mmda_id;
  const userRole = 'MMDA';



  const fetchData = async (endpoint = selectedTable.endpoint) => {
    if (!endpoint) {
      await fetchDashboardData();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Add MMDA filtering to all API calls
      const url = `${API_BASE_URL}${endpoint}?userRole=${userRole}&mmda_id=${userMmdaId}`;
      const response = await axios.get(url, API_CONFIG);
      setData(response.data);
    } catch (err) {

      setError('Failed to fetch data. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch regional data only
      const [usersRes, transactionsRes, zonesRes, groupsRes, officersRes, tradersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/users?userRole=${userRole}&mmda_id=${userMmdaId}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/transactions?userRole=${userRole}&mmda_id=${userMmdaId}&date=${today}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/zones?userRole=${userRole}&mmda_id=${userMmdaId}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/zonal-groups?userRole=${userRole}&mmda_id=${userMmdaId}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/revenue-officers?userRole=${userRole}&mmda_id=${userMmdaId}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/traders?userRole=${userRole}&mmda_id=${userMmdaId}`, API_CONFIG),
      ]);

      const usersData = usersRes.data;
      const transactionsData = transactionsRes.data;
      const zonesData = zonesRes.data;
      const groupsData = groupsRes.data;
      const officersData = officersRes.data;
      const tradersData = tradersRes.data;

      // Process user types data for regional users only
      const userTypes = [
        { name: 'Revenue Officers', count: officersData.length, color: '#FF6384' },
        { name: 'Traders', count: tradersData.length, color: '#36A2EB' },
        { name: 'Other Users', count: 0, color: '#FFCE56' },
      ];

      // Count other users (non-revenue officers, non-traders)
      usersData.forEach(user => {
        const roleName = (user.role_name || '').toLowerCase();
        if (!roleName.includes('revenue') && !roleName.includes('officer') && !roleName.includes('trader')) {
          userTypes[2].count++;
        }
      });

      // Zone and group data for the region
      const zoneGroupData = [
        { name: 'Zones', count: zonesData.length, color: '#FF6384' },
        { name: 'Groups', count: groupsData.length, color: '#36A2EB' },
      ];

      // Transaction distribution for the region
      const transactionDistribution = [
        { name: 'Successful', count: 0, color: '#4BC0C0' },
        { name: 'Pending', count: 0, color: '#FFCE56' },
        { name: 'Failed', count: 0, color: '#FF6384' },
      ];

      transactionsData.forEach(transaction => {
        const status = transaction.status?.toLowerCase() || 'pending';
        if (status === 'successful' || status === 'success') {
          transactionDistribution[0].count++;
        } else if (status === 'failed' || status === 'error') {
          transactionDistribution[2].count++;
        } else {
          transactionDistribution[1].count++;
        }
      });

      setDashboardData({
        usersCount: usersData.length,
        transactionsCount: transactionsData.length,
        zonesCount: zonesData.length,
        userTypesData: userTypes,
        zoneGroupData: zoneGroupData,
        transactionDistribution: transactionDistribution,
      });

    } catch (err) {

      setError('Failed to fetch dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTable]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setFilteredData([]);
      return;
    }

    let filtered = data;
    const currentSearchTerm = getCurrentSearchTerm();

    // Apply search filter
    if (currentSearchTerm) {
      filtered = filtered.filter(item =>
        Object.values(item).some(value =>
          value && value.toString().toLowerCase().includes(currentSearchTerm.toLowerCase())
        )
      );
    }

    // Apply status filter
    if (statusFilter !== 'all' && selectedTable.name === 'Users') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    setFilteredData(filtered);
    setCurrentPage(0);
  }, [data, searchTerms, statusFilter, selectedTable]);

  useEffect(() => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPagedFilteredData(filteredData.slice(startIndex, endIndex));
    setPageCount(Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  }, [filteredData, currentPage]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Function to handle going back to super admin dashboard
  const handleBackToSuperAdmin = () => {
    if (user.is_super_admin_session && user.original_user && onUserChange) {

      onUserChange(user.original_user);
    }
  };

  const showPopup = (message, type = 'success') => {
    // Implement popup notification
    console.log(`${type}: ${message}`);
  };

  const renderContent = () => {
    switch (selectedTable.name) {
      case 'Dashboard':
        return (
          <DashboardView
            dashboardData={dashboardData}
            error={error}
            userTypesData={dashboardData.userTypesData}
            userInfo={user}
            API_BASE_URL={API_BASE_URL}
            auth={API_CONFIG}
            onNavigate={(tableName) => {
              // First check if it's a direct table match
              const directTable = tables.find(t => t.name === tableName);
              if (directTable) {
                setSelectedTable(directTable);
                return;
              }

              // Then check if it's a sub-item
              for (const table of tables) {
                if (table.subItems) {
                  const subItem = table.subItems.find(sub => sub.name === tableName);
                  if (subItem) {
                    setSelectedTable({ ...subItem, parentName: table.name });
                    return;
                  }
                }
              }
            }}
          />
        );
      case 'Users':
        return (
          <UsersView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            currentPage={currentPage}
            totalRows={filteredData.length}
            itemsPerPage={ITEMS_PER_PAGE}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
            setViewUser={setViewUser}
            setIsViewUserModalOpen={setIsViewUserModalOpen}
            setIsAddUserModalOpen={setIsAddUserModalOpen}
            setEditUser={setEditUser}
            setIsEditUserModalOpen={setIsEditUserModalOpen}
          />
        );
      case 'My Zones':
        return (
          <ZonesView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            setIsAddZoneModalOpen={setIsAddZoneModalOpen}
            setEditZone={setEditZone}
            setIsEditZoneModalOpen={setIsEditZoneModalOpen}
            setViewZone={setViewZone}
            setIsViewZoneModalOpen={setIsViewZoneModalOpen}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Groups':
        return (
          <GroupsView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            setIsAddGroupModalOpen={setIsAddGroupModalOpen}
            setEditGroup={setEditGroup}
            setIsEditGroupModalOpen={setIsEditGroupModalOpen}
            setViewGroup={setViewGroup}
            setIsViewGroupModalOpen={setIsViewGroupModalOpen}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Revenue Officers':
        return (
          <RevenueOfficersView
            pagedFilteredData={pagedFilteredData}
            allData={filteredData} // Pass all filtered data for export
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            setIsAddRevenueOfficerModalOpen={setIsAddRevenueOfficerModalOpen}
            setEditRevenueOfficer={setEditRevenueOfficer}
            setIsEditRevenueOfficerModalOpen={setIsEditRevenueOfficerModalOpen}
            setPerformanceOfficer={setPerformanceOfficer}
            setIsPerformanceModalOpen={setIsPerformanceModalOpen}
            setReassignOfficer={setReassignOfficer}
            setIsReassignModalOpen={setIsReassignModalOpen}
            setViewOfficer={setViewOfficer}
            setIsViewOfficerModalOpen={setIsViewOfficerModalOpen}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
            user={user}
            zones={[]} // Pass empty array for now
            mmdaOptions={[]} // Pass empty array for now
            showPopup={showPopup}
            fetchData={fetchData}
          />
        );
      case 'Traders':
        return (
          <TradersView
            pagedFilteredData={pagedFilteredData}
            allData={filteredData} // Pass all filtered data for export
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
            setViewTrader={setViewTrader}
            setIsViewTraderModalOpen={setIsViewTraderModalOpen}
            setIsAddTraderModalOpen={setIsAddTraderModalOpen}
            setReassignTrader={setReassignTrader}
            setIsReassignTraderModalOpen={setIsReassignTraderModalOpen}
            readOnly={false} // MMDA admins can now register traders
          />
        );
      case 'Devices':
        return (
          <DevicesView
            data={filteredData}
            searchTerm={getCurrentSearchTerm()}
            onSearchChange={setCurrentSearchTerm}
            onRefresh={fetchData}
            user={user}
          />
        );
      case 'Payment Methods':
        return (
          <PaymentMethodsView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Receipts':
        return (
          <ReceiptsView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Transactions':
        return (
          <TransactionsView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Audit Logs':
        return (
          <AuditLogsView
            pagedFilteredData={pagedFilteredData}
            searchTerm={getCurrentSearchTerm()}
            setSearchTerm={setCurrentSearchTerm}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            loading={loading}
            userRole={userRole}
            userMmdaId={userMmdaId}
          />
        );
      case 'Support':
        return (
          <SupportTicketsView
            userInfo={user}
            API_BASE_URL={API_BASE_URL}
            auth={API_CONFIG}
          />
        );
      default:
        return <div>Select a table to view data</div>;
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' }}>
      <Sidebar
        tables={tables}
        selectedTable={selectedTable}
        setSelectedTable={setSelectedTable}
        user={user}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <main style={{ flexGrow: 1, padding: 20, overflowY: 'auto', maxWidth: isSidebarOpen ? 'calc(100% - 280px)' : '100%' }}>
        <Header
          user={user}
          handleLogout={handleLogout}
          onBackToSuperAdmin={user.is_super_admin_session ? handleBackToSuperAdmin : null}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          onOpenChat={handleOpenChat}
          onOpenSupport={handleOpenSupport}
        />

        {/* Page Title */}
        <h2 style={{
          marginBottom: 20,
          color: '#1976d2',
          fontWeight: 'bold',
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          {selectedTable.name || 'Dashboard'}
        </h2>

        {renderContent()}

        {/* MMDA-specific Modals */}
        <AddZoneModal
          isOpen={isAddZoneModalOpen}
          onClose={() => setIsAddZoneModalOpen(false)}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <EditZoneModal
          isOpen={isEditZoneModalOpen}
          onClose={() => setIsEditZoneModalOpen(false)}
          zone={editZone}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <ViewZoneModal
          isOpen={isViewZoneModalOpen}
          onClose={() => setIsViewZoneModalOpen(false)}
          zone={viewZone}
        />

        <AddGroupModal
          isOpen={isAddGroupModalOpen}
          onClose={() => setIsAddGroupModalOpen(false)}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <EditGroupModal
          isOpen={isEditGroupModalOpen}
          onClose={() => setIsEditGroupModalOpen(false)}
          group={editGroup}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <ViewGroupModal
          isOpen={isViewGroupModalOpen}
          onClose={() => setIsViewGroupModalOpen(false)}
          group={viewGroup}
        />

        <AddRevenueOfficerModal
          isOpen={isAddRevenueOfficerModalOpen}
          onClose={() => setIsAddRevenueOfficerModalOpen(false)}
          showPopup={showPopup}
          fetchData={fetchData}
          userMmdaId={userMmdaId}
          user={user}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <EditRevenueOfficerModal
          isOpen={isEditRevenueOfficerModalOpen}
          onClose={() => setIsEditRevenueOfficerModalOpen(false)}
          officer={editRevenueOfficer}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <OfficerPerformanceModal
          isOpen={isPerformanceModalOpen}
          onClose={() => setIsPerformanceModalOpen(false)}
          officer={performanceOfficer}
          userMmdaId={userMmdaId}
          user={user}
        />

        <ReassignOfficerModal
          isOpen={isReassignModalOpen}
          onClose={() => setIsReassignModalOpen(false)}
          officer={reassignOfficer}
          showPopup={showPopup}
          fetchData={fetchData}

          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <UserDetailsModal
          isOpen={isViewUserModalOpen}
          onClose={() => setIsViewUserModalOpen(false)}
          user={viewUser}
        />

        <EditUserModal
          isOpen={isEditUserModalOpen}
          onClose={() => setIsEditUserModalOpen(false)}
          user={editUser}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <ViewRevenueOfficerModal
          isOpen={isViewOfficerModalOpen}
          onClose={() => setIsViewOfficerModalOpen(false)}
          officer={viewOfficer}
        />

        <ViewTraderModal
          isOpen={isViewTraderModalOpen}
          onClose={() => setIsViewTraderModalOpen(false)}
          trader={viewTrader}
          onEdit={(trader) => {
            setEditTrader(trader);
            setIsEditTraderModalOpen(true);
          }}
        />

        <AddTraderModal
          isOpen={isAddTraderModalOpen}
          onClose={() => setIsAddTraderModalOpen(false)}
          showPopup={showPopup}
          fetchData={fetchData}

          userMmdaId={userMmdaId}
          user={user}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <EditTraderModal
          isOpen={isEditTraderModalOpen}
          onClose={() => setIsEditTraderModalOpen(false)}
          trader={editTrader}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          user={user}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        <ReassignTraderModal
          isOpen={isReassignTraderModalOpen}
          onClose={() => setIsReassignTraderModalOpen(false)}
          trader={reassignTrader}
          onSuccess={() => {
            fetchData();
            showResultMessage('success', 'Reassigned', 'Trader', reassignTrader?.business_name, 'Trader has been successfully moved to the new zone/group assignment.');
            setShowResultModal(true);
          }}
        />

        <AddUserModal
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          showPopup={showPopup}
          fetchData={fetchData}
          API_BASE_URL={API_BASE_URL}
          userMmdaId={userMmdaId}
          user={user}
          onSuccess={handleOperationSuccess}
          onError={handleOperationError}
        />

        {/* Result Modal for Operation Feedback */}
        <ResultModal
          isOpen={showResultModal}
          onClose={() => setShowResultModal(false)}
          type={resultModalData.type}
          operation={resultModalData.operation}
          itemType={resultModalData.itemType}
          itemName={resultModalData.itemName}
          details={resultModalData.details}
        />

        {/* Chat Modal for Notification Bell */}
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          user={user}
          userRole="mmda_admin"
        />
      </main>

      {/* Chat Button */}
      <ChatButton user={user} userRole="mmda_admin" />
    </div>
  );
};

export default MMDAAdminDashboard;
